import React, { useState, useEffect } from 'react';
import { architectAPI } from '../services/api';
import toast from 'react-hot-toast';

export default function PendingQuestions() {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [approving, setApproving] = useState(false);

  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    setLoading(true);
    try {
      const response = await architectAPI.getPendingQuestions();
      setQuestions(response.data?.questions || []);
      setSelectedQuestions([]);
    } catch (error) {
      toast.error(`Failed to load questions: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelection = (questionId) => {
    setSelectedQuestions(prev =>
      prev.includes(questionId)
        ? prev.filter(id => id !== questionId)
        : [...prev, questionId]
    );
  };

  const handleApprove = async () => {
    if (selectedQuestions.length === 0) {
      toast.error('Please select at least one question');
      return;
    }

    setApproving(true);
    const toastId = toast.loading(`Approving ${selectedQuestions.length} question(s)...`);

    try {
      await architectAPI.approveQuestions({ question_ids: selectedQuestions });
      toast.success(`${selectedQuestions.length} question(s) approved!`, { id: toastId });
      await loadQuestions();
    } catch (error) {
      toast.error(`Failed to approve: ${error.response?.data?.message || error.message}`, { id: toastId });
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (selectedQuestions.length === 0) {
      toast.error('Please select at least one question');
      return;
    }

    if (!window.confirm(`Are you sure you want to reject ${selectedQuestions.length} question(s)?`)) {
      return;
    }

    setApproving(true);
    const toastId = toast.loading(`Rejecting ${selectedQuestions.length} question(s)...`);

    try {
      await architectAPI.rejectQuestions({ question_ids: selectedQuestions });
      toast.success(`${selectedQuestions.length} question(s) rejected`, { id: toastId });
      await loadQuestions();
    } catch (error) {
      toast.error(`Failed to reject: ${error.response?.data?.message || error.message}`, { id: toastId });
    } finally {
      setApproving(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Modern Header */}
      <div className="relative bg-gradient-to-r from-amber-600 via-orange-700 to-red-800 rounded-3xl shadow-2xl p-8 mb-8 overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full blur-3xl"></div>
        
        <div className="relative flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 shadow-xl">
              <svg className="h-12 w-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </div>
            <div className="text-white">
              <h2 className="text-3xl font-bold mb-1">Pending Questions ⏳</h2>
              <p className="text-white/90">
                Review and approve AI-generated questions • {questions.length} question set(s)
              </p>
            </div>
          </div>
          <button
            onClick={loadQuestions}
            className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-6 py-3 rounded-xl font-semibold shadow-lg transition-all hover:scale-105"
          >
            <svg className="h-5 w-5 inline mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Selection Bar */}
      {selectedQuestions.length > 0 && (
        <div className="bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-2xl p-6 mb-8 shadow-lg animate-fade-in">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-3">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </div>
              <span className="text-lg font-bold text-gray-900">
                {selectedQuestions.length} question(s) selected
              </span>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleApprove}
                disabled={approving}
                className="inline-flex items-center px-6 py-3 border-2 border-transparent text-sm font-bold rounded-xl text-white bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 shadow-lg hover:shadow-xl transition-all hover:scale-105"
              >
                <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Approve Selected
              </button>
              <button
                onClick={handleReject}
                disabled={approving}
                className="inline-flex items-center px-6 py-3 border-2 border-transparent text-sm font-bold rounded-xl text-white bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 disabled:opacity-50 shadow-lg hover:shadow-xl transition-all hover:scale-105"
              >
                <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Reject Selected
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-amber-600"></div>
            <div className="absolute top-0 left-0 h-16 w-16 rounded-full bg-amber-100"></div>
          </div>
        </div>
      ) : questions.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
          <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-full p-6 inline-block mb-4">
            <svg className="h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No pending questions</h3>
          <p className="text-gray-500">
            Questions will appear here after articles are processed
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {questions.map((questionSet, setIdx) => (
            <div key={questionSet._id || setIdx} className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100 hover:border-indigo-200 transition-all">
              {/* Article Header */}
              <div className="bg-gradient-to-r from-gray-50 via-blue-50 to-indigo-50 px-6 py-5 border-b-2 border-gray-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <input
                        type="checkbox"
                        checked={selectedQuestions.includes(questionSet._id)}
                        onChange={() => toggleSelection(questionSet._id)}
                        className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer"
                      />
                      <h3 className="text-lg font-bold text-gray-900">{questionSet.article_title}</h3>
                    </div>
                    <div className="flex items-center gap-3 text-sm ml-8">
                      <span className="flex items-center text-gray-600">
                        <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                        </svg>
                        {questionSet.article_source}
                      </span>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
                        {questionSet.exam_type}
                      </span>
                      {questionSet.generated_at && (
                        <span className="flex items-center text-gray-500">
                          <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {new Date(questionSet.generated_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Questions List */}
              <div className="divide-y divide-gray-100">
                {questionSet.questions?.map((q, qIdx) => {
                  const optionsArray = q.options ? Object.entries(q.options).map(([key, value]) => ({ key, value })) : [];
                  const correctIndex = optionsArray.findIndex(opt => opt.key === q.correct_answer);
                  
                  return (
                    <div key={qIdx} className="p-6 hover:bg-gray-50 transition-colors">
                      {/* Question Header */}
                      <div className="flex items-center gap-2 mb-4">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold text-sm">
                          {qIdx + 1}
                        </span>
                        <span className="inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-sm">
                          {q.exam_type || questionSet.exam_type}
                        </span>
                        <span className={`inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold shadow-sm ${
                          q.difficulty === 'Easy' ? 'bg-green-100 text-green-800' :
                          q.difficulty === 'Hard' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {q.difficulty || 'Medium'}
                        </span>
                        {q.topic && (
                          <span className="inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold bg-purple-100 text-purple-800 shadow-sm">
                            📚 {q.topic}
                          </span>
                        )}
                      </div>

                      {/* Question Text */}
                      <div className="mb-4">
                        <p className="text-base font-semibold text-gray-900 leading-relaxed">{q.question}</p>
                      </div>

                      {/* Options */}
                      <div className="space-y-3 mb-4">
                        {optionsArray.map((option, optIdx) => (
                          <div
                            key={option.key}
                            className={`group flex items-start p-4 rounded-xl border-2 transition-all ${
                              option.key === q.correct_answer
                                ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-300 shadow-md'
                                : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'
                            }`}
                          >
                            <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg font-bold mr-3 ${
                              option.key === q.correct_answer
                                ? 'bg-green-600 text-white'
                                : 'bg-gray-100 text-gray-700 group-hover:bg-gray-200'
                            }`}>
                              {option.key}
                            </span>
                            <span className="text-gray-700 flex-1 leading-relaxed">{option.value}</span>
                            {option.key === q.correct_answer && (
                              <div className="flex-shrink-0 ml-2">
                                <div className="bg-green-600 rounded-full p-1">
                                  <svg className="h-4 w-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Explanation */}
                      {q.explanation && (
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
                          <div className="flex items-start">
                            <div className="bg-blue-600 rounded-lg p-2 mr-3">
                              <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                              </svg>
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-bold text-gray-900 mb-1">💡 Explanation:</p>
                              <p className="text-sm text-gray-700 leading-relaxed">{q.explanation}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
